import { createSelector, createSlice } from "@reduxjs/toolkit";

const initialState = {
  byId: {},
  // byId shape per applicationId:
  // {
  //   EXPERIENCE_BASED: { regenProgress: null | { percentage: number, message: string }, liveData: null | {...} },
  //   SCREENING:  { regenProgress: null | { percentage: number, message: string }, liveData: null | {...} },
  //   needsRefetch: false,
  // }
};

const ensureSlot = (state, applicationId, questionType) => {
  if (!state.byId[applicationId]) state.byId[applicationId] = { needsRefetch: false };
  if (!state.byId[applicationId][questionType]) {
    state.byId[applicationId][questionType] = { regenProgress: null, liveData: null };
  }
};

const InterviewSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {
    setProgress: (state, action) => {
      const { applicationId, questionType, progress } = action.payload;
      ensureSlot(state, applicationId, questionType);
      state.byId[applicationId][questionType].regenProgress = progress;
    },

    resetProgress: (state, action) => {
      const { applicationId, questionType } = action.payload;
      ensureSlot(state, applicationId, questionType);
      state.byId[applicationId][questionType].regenProgress = null;
    },

    setLiveData: (state, action) => {
      const { applicationId, questionType, data } = action.payload;
      ensureSlot(state, applicationId, questionType);
      state.byId[applicationId][questionType].liveData = data;
    },

    setNeedsRefetch: (state, action) => {
      const { applicationId, value } = action.payload;
      if (!state.byId[applicationId]) state.byId[applicationId] = { needsRefetch: false };
      state.byId[applicationId].needsRefetch = value;
    },
  },
});

export const { setProgress, resetProgress, setLiveData, setNeedsRefetch } = InterviewSlice.actions;

// Memoized: `useSelector` compares results with reference equality, and a
// selector that builds a fresh object on every call makes the store update
// → render → selector → new reference → render loop that ends in React's
// "Maximum update depth exceeded" (#310). createSelector reuses the last
// result until `byId[applicationId]` itself changes, so the instance is
// cached per applicationId to keep the output reference stable between
// renders (createSelector's memo is per-instance).
const interviewStateSelectors = new Map();

export const selectInterviewState = (applicationId) => {
  let selector = interviewStateSelectors.get(applicationId);
  if (!selector) {
    selector = createSelector(
      (state) => state.interview.byId[applicationId],
      (entry) => {
        const result = { needsRefetch: entry?.needsRefetch ?? false };
        for (const [key, value] of Object.entries(entry ?? {})) {
          if (key === "needsRefetch") continue;
          result[key] = value ?? { regenProgress: null, liveData: null };
        }
        return result;
      },
    );
    interviewStateSelectors.set(applicationId, selector);
  }
  return selector;
};

export default InterviewSlice.reducer;
